import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback, useRef} from "react";
import {ReactEditor, useSlate} from "slate-react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {useGuideState} from "@khan/guide-state";
import {useLocation} from "@khan/urls";

import {toolbarHeight} from "./constants.ts";
import {downloadAsDocx} from "./export/download.ts";
import printContents from "./export/print-contents.ts";
import {getDocumentTitle} from "./slate/helpers/editor-helpers.ts";
import {clearUnsavedChanges} from "./slate/helpers/insertion-helpers.ts";
import ToolButtons from "./tool-buttons.tsx";

interface Props {
    readOnly?: boolean;
    // Document action buttons impact the whole document
    // like the print and export buttons.
    showDocumentActionButtons?: boolean;
}

export default function Toolbar(props: Props): React.ReactElement {
    const {readOnly, showDocumentActionButtons} = props;

    const editor = useSlate();
    const navigation = useGuideState("navigation");
    const threadId = useGuideState("threadId");
    const {href} = useLocation();
    const ref = useRef<HTMLDivElement>(null);

    const toolbarWidth = ref.current?.offsetWidth ?? null;
    const isSmallToolbar = toolbarWidth !== null && toolbarWidth < 300;

    const handlePrintClicked = useCallback(() => {
        clearUnsavedChanges(editor);

        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideCoeditorPrintClicked",
            eventSchemaVersion: 2,
            aiGuideThreadId: threadId ?? "",
            aiGuideNavigationId: navigation.id,
            currentPageURL: href,
        });

        const editorDOMElement = ReactEditor.toDOMNode(editor, editor);
        const title = getDocumentTitle(editor);

        printContents(editorDOMElement, title);
    }, [editor, navigation.id, href, threadId]);

    const handleSaveDocClicked = useCallback(() => {
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideCoeditorExportClicked",
            eventSchemaVersion: 1,
            aiGuideThreadId: threadId ?? "",
            aiGuideNavigationId: navigation.id,
            currentPageURL: href,
        });

        downloadAsDocx(editor);
    }, [editor, navigation.id, href, threadId]);

    return (
        <View
            role="toolbar"
            ref={ref}
            style={[
                styles.toolbar,
                showDocumentActionButtons && styles.withPrintExportButtons,
            ]}
        >
            {!isSmallToolbar && (
                <ToolButtons toolbarWidth={toolbarWidth} readOnly={readOnly} />
            )}
            {showDocumentActionButtons && (
                <>
                    <div style={{flexGrow: 1}} />
                    <Button
                        onClick={handlePrintClicked}
                        size="small"
                        kind="tertiary"
                        disabled={readOnly}
                    >
                        {t`Print`}
                    </Button>
                    <Strut size={spacing.small_12} />
                    <Button
                        onClick={handleSaveDocClicked}
                        disabled={readOnly}
                        size="small"
                    >
                        {t`Export`}
                    </Button>
                </>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    toolbar: {
        flexDirection: "row",
        alignItems: "center",
        padding: spacing.xxxSmall_4,
        height: toolbarHeight,
        flexGrow: 0,
        position: "sticky",
        top: 0,
        zIndex: 10,
        justifyContent: "center",
        borderBlockEnd: `1px solid ${color.offBlack8}`,
        background: color.white,
        borderStartStartRadius: spacing.xxxSmall_4,
        borderStartEndRadius: spacing.xxxSmall_4,
    },
    withPrintExportButtons: {
        paddingInlineStart: spacing.xSmall_8,
        paddingInlineEnd: spacing.xSmall_8,
    },
});
